import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface TaxRow {
  readonly taxId: string;
  readonly taxCode: string;
  readonly taxName: string;
  readonly taxType: string;
  readonly countryCode: string | null;
  readonly region: string | null;
  readonly ratePct: string;
  readonly status: string;
}

const TAX_TYPES = ['GST', 'VAT', 'SALES_TAX', 'WITHHOLDING', 'REGIONAL'];
const TAX_STATUSES = ['ACTIVE', 'INACTIVE', 'ARCHIVED'];
const PAGE = 25;

/**
 * M44 §12 Tax Configuration Screen (§28). Commercial operators configure the
 * tax rules that the billing engine resolves (BR-04). Viewing needs
 * M44_TAX_VIEW; create / update need M44_TAX_MANAGE (SoD).
 */
export function TaxConfigurationScreen(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { token } = useAuth();
  const [rows, setRows] = useState<TaxRow[]>([]);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [taxName, setTaxName] = useState('');
  const [taxType, setTaxType] = useState('GST');
  const [countryCode, setCountryCode] = useState('');
  const [region, setRegion] = useState('');
  const [ratePct, setRatePct] = useState('18');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (typeFilter) params.set('taxType', typeFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (search.trim()) params.set('search', search.trim());
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: TaxRow[]; total: number }>(`/v1/billing/tax?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, typeFilter, statusFilter, search, page, reload]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!taxName.trim()) { setActionError('A tax name is required.'); return; }
    try {
      await apiRequest('/v1/billing/tax', {
        method: 'POST',
        body: {
          taxName: taxName.trim(),
          taxType,
          countryCode: countryCode.trim() || null,
          region: region.trim() || null,
          ratePct: Number(ratePct) || 0,
        },
        token,
      });
      setNotice('Tax rule created.');
      setTaxName('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError('You are not authorised to manage tax (M44_TAX_MANAGE required).');
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="tax-configuration-screen">
      <h1>Tax Configuration</h1>

      <section aria-label="tax-create">
        <h2>Add Tax Rule</h2>
        <form aria-label="tax-form" onSubmit={(e) => void create(e)}>
          <label>Name<input aria-label="field-tax-name" value={taxName} onChange={(e) => setTaxName(e.target.value)} /></label>
          <label>Type
            <select aria-label="field-tax-type" value={taxType} onChange={(e) => setTaxType(e.target.value)}>
              {TAX_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>Country<input aria-label="field-country-code" value={countryCode} onChange={(e) => setCountryCode(e.target.value)} /></label>
          <label>Region<input aria-label="field-region" value={region} onChange={(e) => setRegion(e.target.value)} /></label>
          <label>Rate %<input aria-label="field-rate-pct" value={ratePct} onChange={(e) => setRatePct(e.target.value)} /></label>
          <button type="submit" aria-label="submit-tax">Create</button>
        </form>
        {actionError && <p role="alert" aria-label="tax-action-error">{actionError}</p>}
        {notice && <p aria-label="tax-notice">{notice}</p>}
      </section>

      <section aria-label="tax-filters">
        <label>Type
          <select aria-label="filter-type" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {TAX_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>Status
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {TAX_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Search<input aria-label="filter-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} /></label>
      </section>

      {error && <p role="alert" aria-label="tax-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="tax-total">{total} tax rule(s).</p>
          <table aria-label="tax-table">
            <thead>
              <tr><th scope="col">Code</th><th scope="col">Name</th><th scope="col">Type</th><th scope="col">Country</th><th scope="col">Region</th><th scope="col">Rate %</th><th scope="col">Status</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7}>No tax rules.</td></tr>
              ) : rows.map((t) => (
                <tr key={t.taxId} data-tax={t.taxId}>
                  <td aria-label={`tax-code-${t.taxId}`}>{t.taxCode}</td>
                  <td>{t.taxName}</td>
                  <td>{t.taxType}</td>
                  <td>{t.countryCode ?? '—'}</td>
                  <td>{t.region ?? '—'}</td>
                  <td>{decimal(t.ratePct)}</td>
                  <td aria-label={`tax-status-${t.taxId}`}>{t.status}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="tax-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
